import { Injectable } from '@angular/core'
import { AbstractControl, FormControl, FormGroup } from '@angular/forms'

import { flattenDeep, get, set } from 'lodash'

import { BehaviorSubject, Observable, map } from 'rxjs'

import {
  FormFieldTemplateConfig,
  FormTemplateConfig,
  customComponentSubmitData,
} from '@flowx/core-sdk'

import { buildAsyncValidators, buildFieldValidators } from '../utils/validators'

@Injectable({
  providedIn: 'root',
})
export class FlxFormsService {
  validateComponentStatus = new Map<string, boolean>()

  reusableFormsMap = new Map<string, string[]>() // reusable context -> formIds
  reusableCustomComponentsMap = new Map<string, string[]>() // reusable context -> custom componentIds

  submittedForms$ = new BehaviorSubject<Record<any, boolean>>({})

  formFieldInstancesMap = new Map<
    string,
    { name: string; dataKey: string; control: FormControl }[]
  >()
  formInstancesMap = new Map<string, { name: string; control: FormGroup }[]>()

  buildForm(formConfig: FormTemplateConfig, fieldsConfig: FormFieldTemplateConfig[]): FormGroup {
    const { reusableUiTemplateContext, originalFlowxUuid, flowxUuid, prototypeInstanceMeta } =
      formConfig

    const newForm = new FormGroup({})
    const reusablePrefix = reusableUiTemplateContext ? `${reusableUiTemplateContext}_` : ''

    const metaPath = prototypeInstanceMeta?.map((meta) => meta.index).join('.') ?? '0'
    const formOriginalId = reusablePrefix + originalFlowxUuid
    const formId = reusablePrefix + flowxUuid

    const formInstances = set(this.formInstancesMap.get(formOriginalId) ?? [], metaPath, {
      name: formId,
      control: newForm,
    })
    this.formInstancesMap.set(formOriginalId, formInstances)

    if (reusableUiTemplateContext) {
      this.reusableFormsMap.set(reusableUiTemplateContext, [
        ...(this.reusableFormsMap.get(reusableUiTemplateContext) ?? []),
        formOriginalId,
      ])
    }

    fieldsConfig.forEach((formField) => {
      const {
        reusableUiTemplateContext,
        originalFlowxUuid,
        flowxUuid,
        key,
        prototypeInstanceMeta,
        displayOptions,
      } = formField

      const newFormControl = new FormControl(null, {
        validators: buildFieldValidators(formField),
        asyncValidators: buildAsyncValidators(formField),
        updateOn: displayOptions?.flowxProps?.updateOnBlur ? 'blur' : 'change',
      })

      newForm.addControl(flowxUuid, newFormControl)

      const metaPath = prototypeInstanceMeta?.map((meta) => meta.index).join('.') ?? '0'
      const formFieldOriginalId =
        (reusableUiTemplateContext ? `${reusableUiTemplateContext}_` : '') + originalFlowxUuid

      const fieldInstances = set(
        this.formFieldInstancesMap.get(formFieldOriginalId) ?? [],
        metaPath,
        { name: flowxUuid, dataKey: key, control: newFormControl }
      )
      this.formFieldInstancesMap.set(formFieldOriginalId, fieldInstances)
    })
    return newForm
  }

  getFormControlsAndNames(
    formIds: string[] = [],
    prototypeInstanceMeta?: { index: number; collectionPath: string }[],
    reusableContext: string = null
  ): { control: AbstractControl; name: string }[] {
    return flattenDeep(
      formIds
        // prefix formIds with reusableContext if it exists
        .map((formId) => {
          if (reusableContext) {
            return `${reusableContext}_${formId}`
          }
          return this.reusableFormsMap.get(formId) ?? formId
        })
        .flat()
        .map((formId) => {
          const metaPath = prototypeInstanceMeta?.map((meta) => meta.index).join('.')
          if (this.formFieldInstancesMap.has(formId)) {
            if (metaPath) {
              return get(this.formFieldInstancesMap.get(formId), metaPath)
            }
            return this.formFieldInstancesMap.get(formId)
          }
          if (this.formInstancesMap.has(formId)) {
            if (metaPath) {
              return get(this.formInstancesMap.get(formId), metaPath)
            }
            return this.formInstancesMap.get(formId)
          }
          return []
        })
    ).filter((form) => !!form)
  }

  getFormControlField(
    formUuid: string,
    prototypeInstanceMeta?: { index: number; collectionPath: string }[],
    reusableContext?: string
  ): {
    name: string
    dataKey: string
    control: FormControl
  } | null {
    const metaPath = prototypeInstanceMeta?.map((meta) => meta.index).join('.') ?? 0
    const prefixedFormUuid = reusableContext ? `${reusableContext}_${formUuid}` : formUuid

    return get(this.formFieldInstancesMap.get(prefixedFormUuid), metaPath) ?? null
  }

  getValidateComponents(componentUuids: string[] = [], reusableContext: string = null): any[] {
    return componentUuids
      .map((componentUuid) => {
        // prefix componentUuid with reusableContext if it exists
        if (this.reusableCustomComponentsMap.has(componentUuid)) {
          return this.reusableCustomComponentsMap
            .get(componentUuid)
            .map((reusableComponentUuid) => `${componentUuid}_${reusableComponentUuid}`)
        }
        return componentUuid
      })
      .flat()
      .map((componentUuid) => {
        const prefixedComponentUuid = reusableContext
          ? `${reusableContext}_${componentUuid}`
          : componentUuid
        if (this.validateComponentStatus.has(prefixedComponentUuid)) {
          const isValid = this.validateComponentStatus.get(prefixedComponentUuid)
          return {
            name: prefixedComponentUuid,
            control: { status: isValid ? 'VALID' : 'INVALID' },
          }
        }
        return null
      })
      .filter((control) => control !== null)
  }

  setCustomComponentSubmitData(componentUuid: string, data: any): void {
    customComponentSubmitData.set(componentUuid, () => data)
  }

  formSubmitted$(formId: string, reusableContext: string = null): Observable<boolean> {
    return this.submittedForms$.pipe(
      map((forms) => {
        const prefixedFormId = reusableContext ? `${reusableContext}_${formId}` : formId
        return !!forms[prefixedFormId]
      })
    )
  }

  removeFromValidation(formId: string): void {
    this.validateComponentStatus.delete(formId)

    const submittedCurrentValue = this.submittedForms$.getValue()
    delete submittedCurrentValue[formId]
    this.submittedForms$.next({ ...submittedCurrentValue })
  }

  clearForms(): void {
    this.validateComponentStatus.clear()
    this.reusableFormsMap.clear()
    this.reusableCustomComponentsMap.clear()
    this.submittedForms$.next({})
  }
}
